import { useCallback, useEffect, useState } from 'react';

import type { FundPolicy } from '../../core/fundPolicy/schema';
import { buildDocumentChecklist } from '../../features/documents/application/documentChecklist';
import type {
  DocumentKind,
  UploadedDocument,
} from '../../features/documents/application/ports/documentPort';
import type { MyDependent } from '../../features/employees/application/ports/dependentPort';
import { DocumentsPage } from '../../pages/DocumentsPage';

import { useServices } from '../providers/servicesContext';

/**
 * Connects the documents checklist to its port.
 *
 * The checklist is derived from the policy and the dependants already entered,
 * so it cannot ask for a birth certificate for a child who was never added.
 */
export function DocumentsScreen({
  policy,
  dependents,
}: {
  readonly policy: FundPolicy;
  readonly dependents: readonly MyDependent[];
}) {
  const { documents } = useServices();
  const [uploaded, setUploaded] = useState<readonly UploadedDocument[]>([]);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [viewingKey, setViewingKey] = useState<string | null>(null);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    documents
      .listMyDocuments()
      .then((list) => {
        if (!cancelled) setUploaded(list);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل قائمة الوثائق.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [documents, reloadToken]);

  const requirements = buildDocumentChecklist(
    policy,
    dependents.map(({ id, fullName, relation }) => ({ id, fullName, relation })),
    uploaded,
  );

  const handleUpload = useCallback(
    (key: string, file: File) => {
      // The key encodes which requirement the file answers, because the same
      // kind repeats per dependant — "birth_certificate" alone is ambiguous.
      const [kind, dependentId] = key.split(':') as [DocumentKind, string | undefined];

      setUploadingKey(key);
      setErrorAr(null);

      void documents
        .upload(file, kind, dependentId === '' ? undefined : dependentId)
        .then(() => {
          setReloadToken((token) => token + 1);
        })
        .catch((error: unknown) => {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر رفع الملف.');
        })
        .finally(() => {
          setUploadingKey(null);
        });
    },
    [documents],
  );

  const handleView = useCallback(
    (key: string) => {
      const match = uploaded.find(
        (document) => `${document.kind}:${document.dependentId ?? ''}` === key,
      );
      if (!match) return;

      setViewingKey(key);
      setErrorAr(null);

      void documents
        .getViewUrl(match.id)
        .then((url) => {
          /*
           * A new tab, never an iframe in this origin.
           *
           * The file is whatever an employee uploaded. A PDF can run script,
           * and rendering it inside this page would run it with this page's
           * privileges (§7.15). `noopener` also keeps the opened tab from
           * reaching back through `window.opener`.
           */
          window.open(url, '_blank', 'noopener,noreferrer');
        })
        .catch((error: unknown) => {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر فتح الملف.');
        })
        .finally(() => {
          setViewingKey(null);
        });
    },
    [documents, uploaded],
  );

  return (
    <DocumentsPage
      requirements={requirements.map((requirement) => {
        const match = uploaded.find(
          (document) =>
            document.kind === requirement.kind &&
            document.dependentId === requirement.dependentId,
        );

        return {
          key: `${requirement.kind}:${requirement.dependentId ?? ''}`,
          labelAr: requirement.labelAr,
          forNameAr: requirement.forNameAr,
          satisfied: requirement.satisfied,
          fileNameAr: match?.fileName ?? null,
          sizeBytes: match?.sizeBytes ?? null,
        };
      })}
      uploadingKey={uploadingKey}
      errorAr={errorAr}
      disabled={!policy.employeeSubmissionsEnabled}
      onUpload={handleUpload}
      onView={handleView}
      viewingKey={viewingKey}
    />
  );
}
